import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref as dbRef, onValue, update } from 'firebase/database';
import {
  FaBox,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaCreditCard,
  FaCalendar,
  FaSearch,
  FaFilter,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaImage,
  FaDownload,
  FaMoneyBillWave,
  FaShoppingBag,
  FaClock,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const AdminViewOrder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryDays, setDeliveryDays] = useState('');
  
  // Cancellation modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  // Fetch all orders from Firebase
  useEffect(() => {
    const ordersRef = dbRef(database, 'Dr/orders');

    const unsubscribe = onValue(
      ordersRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const data = snapshot.val();
            const ordersArray = [];

            // Iterate through mobile numbers
            Object.keys(data).forEach((mobileNumber) => {
              const userOrders = data[mobileNumber];

              // Iterate through order numbers for each mobile
              Object.keys(userOrders).forEach((orderNumber) => {
                const order = userOrders[orderNumber];
                ordersArray.push({
                  ...order,
                  mobileNumber: mobileNumber,
                  orderNumberKey: orderNumber,
                });
              });
            });

            // Sort by creation date (newest first)
            ordersArray.sort((a, b) => b.createdAt - a.createdAt);

            setOrders(ordersArray);
            setLoading(false);
          } else {
            setOrders([]);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error fetching orders:', err);
          setError('Failed to load orders');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Firebase error:', error);
        setError('Failed to connect to database');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter orders based on search and status
  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      searchQuery === '' ||
      order.orderId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.userMobile?.includes(searchQuery) ||
      order.product?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || order.orderStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Open confirmation modal
  const handleOpenConfirmModal = (order) => {
    setSelectedOrder(order);
    setShowConfirmModal(true);
    setDeliveryDate('');
    setDeliveryDays('');
  };

  // Open cancellation modal
  const handleOpenCancelModal = (order) => {
    setSelectedOrder(order);
    setShowCancelModal(true);
    setCancellationReason('');
  };

  // Confirm order with delivery details
  const handleConfirmOrder = async () => {
    if (!deliveryDate || !deliveryDays) {
      alert('Please fill in all delivery details');
      return;
    }

    try {
      const orderRef = dbRef(
        database,
        `Dr/orders/${selectedOrder.mobileNumber}/${selectedOrder.orderNumberKey}`
      );
      await update(orderRef, {
        orderStatus: 'Confirmed',
        paymentStatus: 'Verified',
        deliveryDetails: {
          ...selectedOrder.deliveryDetails,
          expectedDeliveryDate: deliveryDate,
          deliveryDays: deliveryDays,
        },
        confirmedAt: Date.now(),
        updatedAt: Date.now(),
      });
      
      setShowConfirmModal(false);
      setShowDetailsModal(false);
      alert('Order confirmed successfully!');
    } catch (error) {
      console.error('Error confirming order:', error);
      alert('Failed to confirm order');
    }
  };

  // Cancel order with reason
  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      alert('Please provide a cancellation reason');
      return;
    }

    try {
      const orderRef = dbRef(
        database,
        `Dr/orders/${selectedOrder.mobileNumber}/${selectedOrder.orderNumberKey}`
      );
      await update(orderRef, {
        orderStatus: 'Cancelled',
        cancellationReason: cancellationReason,
        cancelledAt: Date.now(),
        updatedAt: Date.now(),
      });
      
      setShowCancelModal(false);
      setShowDetailsModal(false);
      alert('Order cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Failed to cancel order');
    }
  };

  // Mark order as dispatched
  const handleMarkDispatched = async (order) => {
    try {
      const orderRef = dbRef(
        database,
        `Dr/orders/${order.mobileNumber}/${order.orderNumberKey}`
      );
      await update(orderRef, {
        orderStatus: 'Dispatched',
        dispatchedAt: Date.now(),
        updatedAt: Date.now(),
      });
      alert('Order marked as dispatched!');
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status');
    }
  };

  // View order details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'Pending Verification':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Cancelled':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Dispatched':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Delivered':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center">
            <FaBox className="mr-3 text-teal-600" />
            Order Management
          </h1>
          <p className="text-gray-600">View and manage all customer orders</p>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Order ID, Customer Name, Mobile..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <FaFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-all appearance-none bg-white"
              >
                <option value="all">All Orders</option>
                <option value="Pending Verification">Pending Verification</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Dispatched">Dispatched</option>
                <option value="Delivered">Delivered</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">Total Orders</p>
              <p className="text-2xl font-bold text-blue-800">{orders.length}</p>
            </div>
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
              <p className="text-sm text-yellow-600 font-medium">Pending</p>
              <p className="text-2xl font-bold text-yellow-800">
                {orders.filter((o) => o.orderStatus === 'Pending Verification').length}
              </p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
              <p className="text-sm text-green-600 font-medium">Confirmed</p>
              <p className="text-2xl font-bold text-green-800">
                {orders.filter((o) => o.orderStatus === 'Confirmed').length}
              </p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">Dispatched</p>
              <p className="text-2xl font-bold text-blue-800">
                {orders.filter((o) => o.orderStatus === 'Dispatched').length}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Orders List */}
        {loading ? (
          <div className="text-center py-16">
            <FaSpinner className="text-5xl text-teal-500 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading orders...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <FaExclamationTriangle className="text-5xl text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-16 text-center">
            <FaBox className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No orders found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredOrders.map((order, index) => (
              <motion.div
                key={`${order.mobileNumber}-${order.orderNumberKey}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden border border-gray-200"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-3 py-1 rounded-lg font-bold text-sm">
                          #{order.orderNumberKey}
                        </div>
                        <span className="text-gray-500 text-sm">{order.orderId}</span>
                        <span
                          className={`px-3 py-1 rounded-lg text-xs font-bold border ${getStatusColor(
                            order.orderStatus
                          )}`}
                        >
                          {order.orderStatus}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center text-gray-700">
                          <FaUser className="mr-2 text-teal-500" />
                          <span className="font-medium">{order.userName}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaPhone className="mr-2 text-teal-500" />
                          <span>{order.userMobile}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaShoppingBag className="mr-2 text-teal-500" />
                          <span>{order.product?.name}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaMoneyBillWave className="mr-2 text-teal-500" />
                          <span className="font-bold text-teal-600">
                            ₹{order.product?.totalPrice}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaCreditCard className="mr-2 text-teal-500" />
                          <span>{order.paymentDetails?.method}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaCalendar className="mr-2 text-teal-500" />
                          <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleViewDetails(order)}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-4 py-2 rounded-xl hover:shadow-lg transition-all"
                      >
                        <FaEye />
                        <span>View Details</span>
                      </button>

                      {order.orderStatus === 'Pending Verification' && (
                        <button
                          onClick={() => handleOpenConfirmModal(order)}
                          className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-xl hover:bg-green-600 transition-all"
                        >
                          <FaCheckCircle />
                          <span>Confirm</span>
                        </button>
                      )}

                      {order.orderStatus === 'Confirmed' && (
                        <button
                          onClick={() => handleMarkDispatched(order)}
                          className="flex items-center justify-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-xl hover:bg-blue-600 transition-all"
                        >
                          <FaCheckCircle />
                          <span>Dispatch Order</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-emerald-500 text-white p-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Order Details</h2>
                    <p className="text-teal-100 text-sm">
                      Order #{selectedOrder.orderNumberKey} - {selectedOrder.orderId}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Customer Information */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaUser className="mr-2 text-blue-600" />
                    Customer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Name</p>
                      <p className="font-bold text-gray-800">{selectedOrder.userName}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Mobile Number</p>
                      <p className="font-bold text-gray-800">{selectedOrder.userMobile}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-gray-600 mb-1">Delivery Address</p>
                      <p className="font-medium text-gray-800">
                        {selectedOrder.deliveryDetails?.address}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Contact Phone</p>
                      <p className="font-bold text-gray-800">
                        {selectedOrder.deliveryDetails?.phoneNumber}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Product Information */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaShoppingBag className="mr-2 text-purple-600" />
                    Product Information
                  </h3>
                  <div className="flex items-center gap-4">
                    <img
                      src={selectedOrder.product?.image}
                      alt={selectedOrder.product?.name}
                      className="w-24 h-24 object-cover rounded-xl"
                    />
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-lg mb-2">
                        {selectedOrder.product?.name}
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Category</p>
                          <p className="font-medium text-gray-800">
                            {selectedOrder.product?.category}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Quantity</p>
                          <p className="font-medium text-gray-800">
                            {selectedOrder.product?.quantity}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Price per Unit</p>
                          <p className="font-medium text-gray-800">
                            ₹{selectedOrder.product?.price}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Total Price</p>
                          <p className="font-bold text-purple-600 text-lg">
                            ₹{selectedOrder.product?.totalPrice}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaCreditCard className="mr-2 text-green-600" />
                    Payment Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <p className="text-gray-600 mb-1">Payment Method</p>
                      <p className="font-bold text-gray-800">
                        {selectedOrder.paymentDetails?.method}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">UPI ID</p>
                      <p className="font-bold text-gray-800">
                        {selectedOrder.paymentDetails?.upiId}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Payment Status</p>
                      <p
                        className={`font-bold ${
                          selectedOrder.paymentStatus === 'Verified'
                            ? 'text-green-600'
                            : 'text-yellow-600'
                        }`}
                      >
                        {selectedOrder.paymentStatus}
                      </p>
                    </div>
                  </div>

                  {/* Payment Screenshot */}
                  {selectedOrder.paymentDetails?.screenshotURL && (
                    <div>
                      <p className="text-gray-600 mb-2 flex items-center">
                        <FaImage className="mr-2" />
                        Payment Screenshot
                      </p>
                      <div className="bg-white p-4 rounded-xl">
                        <img
                          src={selectedOrder.paymentDetails.screenshotURL}
                          alt="Payment Screenshot"
                          className="max-w-full max-h-96 mx-auto rounded-lg"
                        />
                        <a
                          href={selectedOrder.paymentDetails.screenshotURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all"
                        >
                          <FaDownload />
                          <span>Download Screenshot</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order Status */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <FaClock className="mr-2 text-gray-600" />
                    Order Status
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Current Status</p>
                      <p
                        className={`font-bold px-3 py-1 rounded-lg inline-block ${getStatusColor(
                          selectedOrder.orderStatus
                        )}`}
                      >
                        {selectedOrder.orderStatus}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Order Date</p>
                      <p className="font-bold text-gray-800">
                        {new Date(selectedOrder.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Status Update Buttons */}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {selectedOrder.orderStatus === 'Pending Verification' && (
                      <>
                        <button
                          onClick={() => handleOpenConfirmModal(selectedOrder)}
                          className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-all"
                        >
                          <FaCheckCircle />
                          <span>Confirm Order</span>
                        </button>
                        <button
                          onClick={() => handleOpenCancelModal(selectedOrder)}
                          className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all"
                        >
                          <FaTimes />
                          <span>Cancel Order</span>
                        </button>
                      </>
                    )}
                    {selectedOrder.orderStatus === 'Confirmed' && (
                      <button
                        onClick={() => handleMarkDispatched(selectedOrder)}
                        className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all"
                      >
                        <FaCheckCircle />
                        <span>Dispatch Order</span>
                        </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowConfirmModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaCheckCircle className="text-3xl text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Confirm Order</h3>
                <p className="text-gray-600">Order #{selectedOrder.orderNumberKey}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Expected Delivery Date *
                  </label>
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Delivery Days *
                  </label>
                  <input
                    type="number"
                    value={deliveryDays}
                    onChange={(e) => setDeliveryDays(e.target.value)}
                    placeholder="Enter number of days (e.g., 3-5)"
                    min="1"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmOrder}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
                >
                  Confirm Order
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancellation Modal */}
      <AnimatePresence>
        {showCancelModal && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExclamationTriangle className="text-3xl text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Cancel Order</h3>
                <p className="text-gray-600">Order #{selectedOrder.orderNumberKey}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Cancellation Reason *
                  </label>
                  <textarea
                    value={cancellationReason}
                    onChange={(e) => setCancellationReason(e.target.value)}
                    placeholder="Please provide a reason for cancellation..."
                    rows="4"
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all"
                >
                  Go Back
                </button>
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
                >
                  Cancel Order
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminViewOrder;
